import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  clearBooking,
  clearBookingPayment,
  clearBookingReservations,
  resetBookingReservationPrice,
  selectBooking,
  setBookingError,
} from "../../features/booking/bookingSlice";
import { datesBetween, getIsoString, getMonths, type IDay, validEndDate } from "../../helpers/date";
import { type IBookingState } from "../../features/booking/bookingInterface";
import { setBookingDates } from "../../features/booking/bookingSlice";
import {
  getConfiguration,
  getHotelPaymentConfiguration,
  getRoomAvailability,
  selectHotel,
} from "../../features/hotel/hotelSlice";
import moment, { type Moment } from "moment";
import { GTMTrackingNoDatesAvailable } from "../../helpers/GTMTracking";
import { useTranslation } from "react-i18next";
import {
  getDailyDateInfo,
  isDateUnavailable,
  rangeHasUnavailableDate,
} from "../../helpers/dailyPrices";
import { formatCurrency } from "../../helpers/currency";
import XMark from "../Svg/XMark";
import { useDailyPrices } from "../../hooks/useDailyPrices";

function DateRange() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const { t } = useTranslation();

  const addMonths = 24;
  const [today, setToday] = useState<any>(getIsoString(moment().utc().startOf("day")));
  const [startRange, setStartRange] = useState<Moment>(moment().utc().startOf("month"));
  const [endRange, setEndRange] = useState<Moment>(moment().utc().add(addMonths, "M").startOf("month"));
  const dailyPrices = useDailyPrices();

  // const months = getMonths(startRange, endRange);
  const [months, setMonths] = useState<
    { Year: number; Number: number; Name: string; Days: IDay[] }[]
  >([]);

  /**
   * Set date range
   */
  useEffect(() => {
    if (import.meta.env?.["VITE_MEWS_" + hotel.HotelCode + "_CALENDAR_START"] !== undefined) {
      setToday(
        getIsoString(
          moment(import.meta.env?.["VITE_MEWS_" + hotel.HotelCode + "_CALENDAR_START"]).startOf(
            "day"
          )
        )
      );
      setStartRange(
        moment(import.meta.env?.["VITE_MEWS_" + hotel.HotelCode + "_CALENDAR_START"]).startOf(
          "month"
        )
      );
      setEndRange(
        moment(import.meta.env?.["VITE_MEWS_" + hotel.HotelCode + "_CALENDAR_START"])
          .add(addMonths, "M")
          .startOf("month")
      );
    } else {
      setToday(getIsoString(moment().utc().startOf("day")));
      setStartRange(moment().utc().startOf("month"));
      setEndRange(moment().utc().add(addMonths, "M").endOf("month"));
    }

    // Slide active dates into view
    // If element exists with class name 'start-day', scroll into view
    setTimeout(() => {
      const element = document.querySelector(".start-day");
      if (element) {
        element.scrollIntoView({ behavior: "instant", block: "center", inline: "center" });
      }
    }, 100);

    // Check if StartUtc is not in the past
    // Clear booking if so
    if (booking.StartUtc !== null && booking.StartUtc < getIsoString(moment().utc().startOf("day"))) {
      dispatch(clearBooking());
      return;
    }

  }, [hotel.HotelCode]);

  useEffect(() => {
    setMonths(getMonths(startRange, endRange));
  }, [startRange, endRange]);

  // Set state booking days and get availability
  const setDates = (StartUtc: IBookingState["StartUtc"], EndUtc: IBookingState["EndUtc"]) => {
    let Dates: IBookingState["Dates"] = [];
    let NightCount: IBookingState["NightCount"] = booking.NightCount;

    // Get available rooms from api
    if (EndUtc !== null) {
      dispatch(getRoomAvailability({ StartUtc, EndUtc, VoucherCode: booking.VoucherCode }));
    } else {
      dispatch(clearBookingReservations());
    }
    if (StartUtc !== null && EndUtc !== null) {
      Dates = datesBetween(StartUtc, EndUtc);
      NightCount = Dates.length - 1;
    }
    // Set booking dates
    dispatch(setBookingDates({ StartUtc, EndUtc, Dates, NightCount }));
    dispatch(setBookingError(null));
    dispatch(resetBookingReservationPrice());
  };

  // Get availability
  useEffect(() => {
    
    if (hotel.RoomCategoryAvailabilities) {
      if (hotel.RoomCategoryAvailabilities.length && booking.EndUtc !== null) {
        // addRoom({ ClearRooms: true });
      }
      if (hotel.RoomCategoryAvailabilities.length === 0 && booking.EndUtc !== null) {
        dispatch(setBookingError(booking.ErrorText));
        // GTM track no dates available
        GTMTrackingNoDatesAvailable();
      }
    } else {
      dispatch(getConfiguration(hotel.ConfigurationId));
      dispatch(getHotelPaymentConfiguration(hotel.Id));
      dispatch(clearBooking());
    }
    dispatch(clearBookingPayment());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel.RoomCategoryAvailabilities, dispatch]);

  // Set selected days when click on a day
  const selectDate = (date: string) => {
    if (isDateUnavailable(dailyPrices, hotel.HotelCode, date)) {
      return;
    }

    if (date >= today) {
      // Start over with date range
      if (booking.StartUtc !== null && booking.EndUtc !== null) {
        setDates(date, null);
      }

      // Set start date
      else if (booking.StartUtc === null) setDates(date, null);
      // Do nothing when the same day is clicked twice
      else if (booking.StartUtc !== null && booking.StartUtc === date) {
        // Do nothing
      }

      // Set end date
      else if (booking.StartUtc !== null && validEndDate(booking.StartUtc, date)) {
        if (
          rangeHasUnavailableDate(
            dailyPrices,
            hotel.HotelCode,
            booking.StartUtc,
            date,
          )
        ) {
          setDates(date, null);
          return;
        }
        setDates(booking.StartUtc, date);
      }

      // Set new start date if date is before start date
      else if (booking.StartUtc !== null && !validEndDate(booking.StartUtc, date)) {
        setDates(date, null);
      }

      // Set first start date
      else {
        setDates(date, null);
      }

      // Reset date range
    } else {
      setDates(null, null);
    }
  };

  const classes = (day: IDay, i: number, unavailable: boolean) => {
    let className = "";
    if (i === 0) {
      if (day.Weekday === 0) {
        className += " col-start-7";
      } else {
        className += " col-start-" + day.Weekday;
      }
    }

    if (day.Date < today || unavailable) {
      className += " border-transparent text-gray-400 line-through pointer-events-none opacity-50";
    } else if (day.Date === booking.StartUtc) {
      className += " border-white border-2 bg-white rounded-md start-day";
      if (
        booking.EndUtc !== null &&
        booking.EndUtc !== booking.StartUtc &&
        !day.LastDay &&
        day.Weekday !== 0
      ) {
        className += " rounded-e-none border-r-0";
      }
    } else if (day.Date === booking.EndUtc) {
      className += " border-white border-2 rounded-md";
      if (day.Number !== "01" && day.Weekday !== 1) className += " rounded-s-none border-l-0";
    } else if (booking.Dates.includes(day.Date)) {
      className += " border-white border-x-0 ";

      if (day.Weekday === 0 && i === 0) className += "";
      else if (i === 0) className += "border-l-2 rounded-l-md";
      else if (day.LastDay) {
        if (day.Weekday !== 1) {
          className += "border-r-2 rounded-r-md";
        }
      } else if (day.Weekday === 1) className += "border-l-2 rounded-l-md";
      else if (day.Weekday === 0) className += "border-r-2 rounded-r-md";
    } else {
      className += " border-transparent";
    }

    if (booking.Dates.length > 1 && booking.Dates.includes(day.Date) && !unavailable) {
      className += " bg-white";
    }

    return className;
  };

  return (
    <div className="flex flex-col gap-y-4 w-full mt-4">
      {months.map((month) => {
        return (
          <div
            key={`${month.Year}-${month.Number}`}
            className="w-full grid grid-cols-1 gap-y-4"
          >
            <p className="text-fluid-base mt-4">
              {t('months.' + month.Number)} {month.Year}
            </p>

            <div className="grid grid-cols-7 gap-y-3 w-full text-fluid-base text-center cursor-pointer">
              {month.Days.map((day, i) => {
                const dateInfo =
                  day.Date >= today
                    ? getDailyDateInfo(
                        dailyPrices,
                        hotel.HotelCode,
                        day.Date,
                        hotel.Culture.CurrencyCode,
                      )
                    : null;
                const unavailable = dateInfo?.available === false;

                return (
                  <div
                    key={day.Date}
                    onClick={() => {
                      selectDate(day.Date);
                    }}
                    className={`p-2 border-2 ${classes(day, i, unavailable)}`}
                    aria-disabled={unavailable || undefined}
                  >
                    <span>{day.Number}</span>
                    {unavailable && (
                      <XMark className="size-fluid-3 mx-auto mt-0.5 text-gray-500" />
                    )}
                    {!unavailable && dateInfo?.price != null && (
                      <span className="text-fluid-xs text-gray-500 block truncate">
                        {formatCurrency(dateInfo.price, 0)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DateRange;
